import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import type { Event, CreateEventRequest, EventType } from "@/types";
import { createInitialFormData } from "@/utils/formHelpers";
import { useFormState } from "@/hooks/useFormState";
import { createCrudHooks } from "@/hooks/crudHooksFactory";
import i18n from "@/i18n/config";

export const useEvents = (
  params?: {
    search?: string;
    userId?: string;
    trackId?: string;
    vehicleId?: string;
    upcoming?: boolean;
    past?: boolean;
    startDateFrom?: string;
    startDateTo?: string;
    limit?: number;
    offset?: number;
    orderBy?: "startDate" | "createdAt" | "name";
    orderDirection?: "asc" | "desc";
  },
  enabled = true,
) => {
  return useQuery({
    queryKey: ["events", params],
    queryFn: () => apiClient.getEvents(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
  });
};

export const useEvent = (id: string) => {
  return useQuery({
    queryKey: ["event", id],
    queryFn: () => apiClient.getEvent(id),
    enabled: !!id,
  });
};

export const useUpcomingEvents = (limit?: number, offset?: number) => {
  return useQuery({
    queryKey: ["events", "upcoming", { limit, offset }],
    queryFn: () => apiClient.getUpcomingEvents(limit, offset),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useVehicleEvents = (vehicleId: string, limit = 3) => {
  return useQuery({
    queryKey: ["events", "vehicle", { vehicleId, limit }],
    queryFn: () =>
      apiClient.getEvents({
        vehicleId,
        past: true,
        limit,
        orderBy: "startDate",
        orderDirection: "desc",
      }),
    enabled: !!vehicleId,
  });
};

const eventCrudHooks = createCrudHooks({
  create: {
    mutationFn: (eventData: CreateEventRequest) =>
      apiClient.createEvent(eventData),
    cacheActions: [
      // Invalidate and refetch events queries
      { type: "invalidate", key: ["events"] },
      // Add the new event to the cache
      { type: "setQueryData", key: (newEvent) => ["event", newEvent.id] },
      { type: "invalidate", key: ["dashboard"] },
    ],
    errorMessageKey: "events:validation.createFailed",
  },
  update: {
    mutationFn: ({
      id,
      eventData,
    }: {
      id: string;
      eventData: Partial<CreateEventRequest>;
    }) => apiClient.updateEvent(id, eventData),
    cacheActions: [
      // Update the specific event in the cache
      {
        type: "setQueryData",
        key: (updatedEvent) => ["event", updatedEvent.id],
      },
      // Invalidate events list to trigger refetch
      { type: "invalidate", key: ["events"] },
      { type: "invalidate", key: ["dashboard"] },
    ],
    errorMessageKey: "events:validation.updateFailed",
  },
  delete: {
    mutationFn: (id: string) => apiClient.deleteEvent(id),
    cacheActions: [
      // Remove the event from the cache
      { type: "removeQueries", key: (_, deletedId) => ["event", deletedId] },
      // Invalidate events list to trigger refetch
      { type: "invalidate", key: ["events"] },
      { type: "invalidate", key: ["dashboard"] },
    ],
    errorMessageKey: "events:validation.deleteFailed",
  },
});

export const useCreateEvent = eventCrudHooks.useCreate;
export const useUpdateEvent = eventCrudHooks.useUpdate;
export const useDeleteEvent = eventCrudHooks.useDelete;

// Validation logic extracted as a pure function for stable reference
function validateEventData(
  data: Partial<CreateEventRequest>,
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!data.trackId?.trim()) {
    errors.trackId = i18n.t("events:validation.trackRequired");
  }

  if (!data.name?.trim()) {
    errors.name = i18n.t("events:validation.nameRequired");
  }

  if (!data.eventType) {
    errors.eventType = i18n.t("events:validation.eventTypeRequired");
  }

  if (!data.startDate) {
    errors.startDate = i18n.t("events:validation.startDateRequired");
  }

  if (!data.endDate) {
    errors.endDate = i18n.t("events:validation.endDateRequired");
  }

  if (data.startDate && data.endDate) {
    const startDate = new Date(data.startDate);
    const endDate = new Date(data.endDate);

    if (startDate >= endDate) {
      errors.endDate = i18n.t("events:validation.endDateAfterStart");
    }
  }

  if (data.maxParticipants && data.maxParticipants < 1) {
    errors.maxParticipants = i18n.t("events:validation.maxParticipantsMin");
  }

  return errors;
}

// Custom hook for managing event form state
export const useEventForm = (initialEvent?: Event) => {
  const getInitialEventData = useCallback(
    () =>
      createInitialFormData(initialEvent, {
        trackId: "",
        name: "",
        description: "",
        startDate: "",
        endDate: "",
        maxParticipants: undefined as number | undefined,
        visibility: "private" as "private" | "invitation_only" | "public",
        eventType: "trackday" as EventType,
      }),
    [initialEvent],
  );

  const {
    data: eventData,
    errors,
    validate,
    updateField,
    resetForm,
    setData: setEventData,
  } = useFormState(getInitialEventData, validateEventData);

  return {
    eventData,
    errors,
    validate,
    updateField,
    resetForm,
    setEventData,
  };
};
